import React from 'react'
import { Order, OrderStatus } from '../../pages/Orders'
import {
  XIcon,
  ClockIcon,
  CheckCircleIcon,
  TruckIcon,
  PackageIcon,
  CalendarIcon,
  CreditCardIcon,
  UserIcon,
  MailIcon,
  PhoneIcon,
  MapPinIcon,
  TagIcon,
  FileTextIcon,
  PrinterIcon,
  DownloadIcon,
  AlertTriangleIcon,
  CheckIcon,
} from 'lucide-react'
interface OrderDetailProps {
  order: Order
  onClose: () => void
  onStatusChange: (newStatus: OrderStatus) => void
}
export function OrderDetail({
  order,
  onClose,
  onStatusChange,
}: OrderDetailProps) {
  // Order statuses for dropdown
  const availableStatuses: OrderStatus[] = [
    'new',
    'in-progress',
    'open',
    'completed',
    'cancelled',
  ]
  // Get payment method name
  const getPaymentMethod = (transaction?: string) => {
    if (!transaction) return 'Unknown payment method'
    if (transaction.startsWith('62')) return 'Credit Card'
    return 'Bank Transfer'
  }
  // Get shipping status
  const getShippingStatus = (status: OrderStatus) => {
    switch (status) {
      case 'new':
        return 'Awaiting processing'
      case 'in-progress':
        return 'Processing'
      case 'open':
        return 'Ready for pickup'
      case 'completed':
        return 'Delivered'
      case 'cancelled':
        return 'Cancelled'
      default:
        return 'Unknown'
    }
  }
  // Format date
  const formatDate = (dateString: string) => {
    const parts = dateString.split('/')
    if (parts.length !== 3) return dateString
    const months = [
      'January',
      'February',
      'March',
      'April',
      'May',
      'June',
      'July',
      'August',
      'September',
      'October',
      'November',
      'December',
    ]
    const day = parseInt(parts[0])
    const month = parseInt(parts[1]) - 1
    const year = parseInt(parts[2])
    return `${day} ${months[month]} ${year}`
  }
  return (
    <div className="fixed inset-0 bg-black bg-opacity-30 z-50 flex justify-end">
      <div className="bg-white w-full max-w-md h-full overflow-y-auto shadow-xl">
        <div className="p-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <h2 className="text-lg font-medium text-gray-900">
            Order #{order.id}
          </h2>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 focus:outline-none"
          >
            <XIcon size={24} />
          </button>
        </div>
        <div className="p-6">
          {/* Order status and date */}
          <div className="mb-6 flex justify-between items-start">
            <div className="flex items-center">
              <div
                className={`w-3 h-3 rounded-full mr-2 ${
                  order.paymentStatus === 'paid'
                    ? 'bg-green-500'
                    : order.paymentStatus === 'cancelled'
                      ? 'bg-red-500'
                      : 'bg-gray-500'
                }`}
              ></div>
              <span className="font-medium">
                {order.paymentStatus.charAt(0).toUpperCase() +
                  order.paymentStatus.slice(1)}
              </span>
            </div>
            <div className="flex flex-col items-end">
              <div className="flex items-center text-gray-500">
                <CalendarIcon size={14} className="mr-1" />
                <span>{formatDate(order.date)}</span>
              </div>
              <div className="flex items-center text-gray-500 mt-1">
                <ClockIcon size={14} className="mr-1" />
                <span>{order.time}</span>
              </div>
            </div>
          </div>
          {/* Order Timeline */}
          <div className="mb-8 border border-gray-200 rounded-lg p-4 bg-gray-50">
            <h3 className="text-sm font-medium text-gray-700 mb-3">
              Order Timeline
            </h3>
            <div className="space-y-3">
              <div className="flex items-start">
                <div className="flex-shrink-0 h-5 w-5 rounded-full bg-blue-500 flex items-center justify-center">
                  <CheckIcon size={12} className="text-white" />
                </div>
                <div className="ml-3">
                  <p className="text-sm font-medium text-gray-900">
                    Order placed
                  </p>
                  <p className="text-xs text-gray-500">
                    {order.date} at {order.time}
                  </p>
                </div>
              </div>
              {order.status === 'in-progress' ||
              order.status === 'completed' ? (
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-amber-500 flex items-center justify-center">
                    <CheckIcon size={12} className="text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      Processing started
                    </p>
                    <p className="text-xs text-gray-500">
                      Order is being processed
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-xs text-white">2</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-400">
                      Processing
                    </p>
                    <p className="text-xs text-gray-500">
                      Waiting to be processed
                    </p>
                  </div>
                </div>
              )}
              {order.status === 'completed' ? (
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-green-500 flex items-center justify-center">
                    <CheckIcon size={12} className="text-white" />
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-900">
                      Completed
                    </p>
                    <p className="text-xs text-gray-500">
                      Order has been fulfilled
                    </p>
                  </div>
                </div>
              ) : (
                <div className="flex items-start">
                  <div className="flex-shrink-0 h-5 w-5 rounded-full bg-gray-300 flex items-center justify-center">
                    <span className="text-xs text-white">3</span>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm font-medium text-gray-400">
                      Completed
                    </p>
                    <p className="text-xs text-gray-500">
                      Waiting to be completed
                    </p>
                  </div>
                </div>
              )}
            </div>
          </div>
          {/* Customer info */}
          <div className="space-y-4 mb-8">
            <h3 className="text-base font-medium text-gray-900 flex items-center">
              <UserIcon size={16} className="mr-2 text-blue-600" />
              Customer Information
            </h3>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="flex items-start mb-3">
                <div className="w-10 h-10 bg-gray-100 rounded-full flex items-center justify-center text-gray-600 mr-3 flex-shrink-0">
                  {order.customer.name.charAt(0).toUpperCase()}
                </div>
                <div>
                  <div className="font-medium text-gray-900">
                    {order.customer.name}
                  </div>
                  <div className="text-sm text-gray-500">
                    Customer #{order.id.split('-')[0]}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-start">
                  <MailIcon size={16} className="mr-2 text-gray-500 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-900">
                      {order.customer.email}
                    </div>
                    <div className="text-xs text-gray-500">Email</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <PhoneIcon size={16} className="mr-2 text-gray-500 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-900">
                      {order.customer.mobileNumber}
                    </div>
                    <div className="text-xs text-gray-500">Mobile</div>
                  </div>
                </div>
                {order.customer.additionalPhone && (
                  <div className="flex items-start">
                    <PhoneIcon
                      size={16}
                      className="mr-2 text-gray-500 mt-0.5"
                    />
                    <div>
                      <div className="text-sm text-gray-900">
                        {order.customer.additionalPhone}
                      </div>
                      <div className="text-xs text-gray-500">
                        Additional Phone
                      </div>
                    </div>
                  </div>
                )}
                <div className="flex items-start">
                  <MapPinIcon size={16} className="mr-2 text-gray-500 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-900">
                      {order.customer.address}
                    </div>
                    {order.customer.address2 && (
                      <div className="text-sm text-gray-900 mt-1">
                        {order.customer.address2}
                      </div>
                    )}
                    <div className="text-xs text-gray-500">
                      Shipping Address
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          {/* Payment Information */}
          <div className="space-y-4 mb-8">
            <h3 className="text-base font-medium text-gray-900 flex items-center">
              <CreditCardIcon size={16} className="mr-2 text-blue-600" />
              Payment Information
            </h3>
            <div className="border border-gray-200 rounded-lg p-4">
              <div className="grid grid-cols-1 gap-3">
                <div className="flex items-start">
                  <CreditCardIcon
                    size={16}
                    className="mr-2 text-gray-500 mt-0.5"
                  />
                  <div>
                    <div className="text-sm text-gray-900">
                      {getPaymentMethod(order.transaction)}
                    </div>
                    <div className="text-xs text-gray-500">Payment Method</div>
                  </div>
                </div>
                <div className="flex items-start">
                  <TagIcon size={16} className="mr-2 text-gray-500 mt-0.5" />
                  <div>
                    <div className="text-sm text-gray-900">
                      {order.paymentStatus === 'paid'
                        ? 'Paid'
                        : order.paymentStatus === 'cancelled'
                          ? 'Cancelled'
                          : 'Unpaid'}
                    </div>
                    <div className="text-xs text-gray-500">Payment Status</div>
                  </div>
                </div>
                {order.transaction && (
                  <div className="flex items-start">
                    <FileTextIcon
                      size={16}
                      className="mr-2 text-gray-500 mt-0.5"
                    />
                    <div>
                      <div className="text-sm text-gray-900">
                        {order.transaction}
                      </div>
                      <div className="text-xs text-gray-500">
                        Transaction ID
                      </div>
                    </div>
                  </div>
                )}
                {order.transactionStatus && (
                  <div className="flex items-start">
                    <div className="mr-2 mt-0.5">
                      {order.transactionStatus === 'confirmed' ? (
                        <CheckCircleIcon size={16} className="text-green-500" />
                      ) : order.transactionStatus === 'failed' ? (
                        <AlertTriangleIcon size={16} className="text-red-500" />
                      ) : (
                        <ClockIcon size={16} className="text-amber-500" />
                      )}
                    </div>
                    <div>
                      <div className="text-sm text-gray-900 capitalize">
                        {order.transactionStatus}
                      </div>
                      <div className="text-xs text-gray-500">
                        Transaction Status
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
          {/* Order details */}
          <div className="border-t border-gray-200 pt-6 mb-8">
            <h3 className="text-base font-medium text-gray-900 flex items-center mb-4">
              <PackageIcon size={16} className="mr-2 text-blue-600" />
              Order Items
            </h3>
            <div className="space-y-4">
              {order.items.map((item, index) => (
                <div
                  key={index}
                  className="flex justify-between border-b border-gray-100 pb-3"
                >
                  <div>
                    <div className="font-medium text-gray-900">{item.name}</div>
                    <div className="text-sm text-gray-500">
                      Quantity: {item.quantity} x RM{item.price.toFixed(2)}
                    </div>
                  </div>
                  <div className="font-medium text-gray-900">
                    RM{(item.price * item.quantity).toFixed(2)}
                  </div>
                </div>
              ))}
              <div className="flex justify-between pt-2">
                <span className="text-gray-700">Subtotal</span>
                <span className="text-gray-900">
                  RM{order.total.toFixed(2)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Shipping</span>
                <span className="text-gray-900">RM0.00</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-700">Tax</span>
                <span className="text-gray-900">RM0.00</span>
              </div>
              <div className="border-t border-gray-200 pt-4 flex justify-between font-medium">
                <span className="text-lg">Total</span>
                <span className="text-lg text-blue-600">
                  RM{order.total.toFixed(2)}
                </span>
              </div>
            </div>
          </div>
          {/* Shipping Status */}
          <div className="mb-8 border border-gray-200 rounded-lg p-4">
            <h3 className="text-sm font-medium text-gray-700 mb-3 flex items-center">
              <TruckIcon size={16} className="mr-2 text-blue-600" />
              Shipping Status
            </h3>
            <div className="text-gray-900 font-medium">
              {getShippingStatus(order.status)}
            </div>
            {order.notes && (
              <div className="mt-2 text-sm text-gray-600 border-t border-gray-200 pt-2">
                <div className="font-medium">Notes:</div>
                <p>{order.notes}</p>
              </div>
            )}
          </div>
          {/* Order status change */}
          <div className="mt-8 border-t border-gray-200 pt-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Change order status
            </label>
            <select
              value={order.status}
              onChange={(e) => onStatusChange(e.target.value as OrderStatus)}
              className="block w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-blue-500 focus:border-blue-500"
            >
              {availableStatuses.map((status) => (
                <option key={status} value={status}>
                  {status.charAt(0).toUpperCase() +
                    status.slice(1).replace('-', ' ')}
                </option>
              ))}
            </select>
          </div>
          {/* Action buttons */}
          <div className="mt-8 flex flex-col space-y-3">
            <button className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-center items-center">
              <CheckCircleIcon size={16} className="mr-2" />
              Mark as Fulfilled
            </button>
            <div className="grid grid-cols-2 gap-3">
              <button className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-center items-center">
                <PrinterIcon size={16} className="mr-2" />
                Print Order
              </button>
              <button className="flex-1 border border-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-center items-center">
                <DownloadIcon size={16} className="mr-2" />
                Export
              </button>
            </div>
            <button className="w-full border border-gray-300 text-gray-700 py-2 px-4 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 flex justify-center items-center">
              <ClockIcon size={16} className="mr-2" />
              View History
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}
// Missing icon
const CheckIcon = ({ size = 24, ...props }) => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
    {...props}
  >
    <polyline points="20 6 9 17 4 12" />
  </svg>
)
